/**
 * Утилиты для обработки ошибок API
 *
 * Централизованная обработка ошибок:
 * - ForecastError для прогнозов
 * - handleApiError для маппинга HTTP-ошибок в человеко-читаемые сообщения
 */

// ============================================================
// ForecastError
// ============================================================

export class ForecastError extends Error {
  public code: string | undefined;
  public status: number | undefined;

  constructor(message: string, code?: string, status?: number) {
    super(message);
    this.name = 'ForecastError';
    this.code = code;
    this.status = status;
    Object.setPrototypeOf(this, ForecastError.prototype);
  }
}

// ============================================================
// ProfileError (для ExtendedProfilePage)
// ============================================================

export class ProfileError extends Error {
  public code: string | undefined;
  public status: number | undefined;

  constructor(message: string, code?: string, status?: number) {
    super(message);
    this.name = 'ProfileError';
    this.code = code;
    this.status = status;
    Object.setPrototypeOf(this, ProfileError.prototype);
  }
}

// ============================================================
// ContactError (для ContactSelector)
// ============================================================

export class ContactError extends Error {
  public code: string | undefined;
  public status: number | undefined;

  constructor(message: string, code?: string, status?: number) {
    super(message);
    this.name = 'ContactError';
    this.code = code;
    this.status = status;
    Object.setPrototypeOf(this, ContactError.prototype);
  }
}

// ============================================================
// GenericApiError (универсальная)
// ============================================================

export class GenericApiError extends Error {
  public code: string | undefined;
  public status: number | undefined;

  constructor(message: string, code?: string, status?: number) {
    super(message);
    this.name = 'GenericApiError';
    this.code = code;
    this.status = status;
    Object.setPrototypeOf(this, GenericApiError.prototype);
  }
}

// ============================================================
// ErrorResponse — тип ответа сервера с ошибкой
// ============================================================

interface ErrorResponse {
  error?: string;
  message?: string;
  code?: string;
  success?: boolean;
}

// ============================================================
// isAxiosLike — проверка на axios-подобный error.response
// ============================================================

interface AxiosLikeError {
  response?: {
    status: number;
    data?: ErrorResponse;
  };
  request?: unknown;
  message?: string;
}

function isAxiosLike(error: unknown): error is AxiosLikeError {
  if (typeof error !== 'object' || error === null) return false;
  const e = error as Record<string, unknown>;
  return 'response' in e || 'request' in e;
}

// ============================================================
// mapStatusToMessage — HTTP status → человеко-читаемое сообщение
// ============================================================

function mapStatusToMessage(status: number, serverMessage?: string): string {
  switch (status) {
    case 400:
      return serverMessage || 'Некорректный запрос';
    case 401:
      return 'Требуется авторизация';
    case 403:
      return 'Нет доступа к этому ресурсу';
    case 404:
      return serverMessage || 'Ресурс не найден';
    case 429:
      return 'Превышен лимит запросов, попробуйте позже';
    case 500:
      return 'Внутренняя ошибка сервера';
    case 502:
      return 'Сервер временно недоступен';
    case 503:
      return 'Сервер на обслуживании, попробуйте позже';
    default:
      return serverMessage || `Произошла ошибка (код ${status})`;
  }
}

// ============================================================
// handleApiError — универсальный маппер ошибок
// ============================================================

export function handleApiError(error: unknown, fallbackMessage = 'Произошла ошибка'): string {
  // Axios-like error
  if (isAxiosLike(error)) {
    const status = error.response?.status;
    const serverMsg = error.response?.data?.error || error.response?.data?.message;

    if (status) {
      return mapStatusToMessage(status, serverMsg);
    }

    if (error.request !== undefined) {
      return 'Нет соединения с сервером';
    }

    return error.message || fallbackMessage;
  }

  // Fetch Response-like error (свойства response/status)
  if (error instanceof TypeError) {
    // TypeError при fetch обычно = network error
    return 'Нет соединения с сервером';
  }

  // Our own error types
  if (error instanceof ForecastError) {
    return error.message;
  }
  if (error instanceof ProfileError) {
    return error.message;
  }
  if (error instanceof ContactError) {
    return error.message;
  }
  if (error instanceof GenericApiError) {
    return error.message;
  }

  // Plain Error object
  if (error instanceof Error) {
    return error.message;
  }

  // String
  if (typeof error === 'string') {
    return error;
  }

  return fallbackMessage;
}

// ============================================================
// handleApiErrorTyped — возвращает конкретный тип ошибки
// ============================================================

type ErrorConstructor = typeof ForecastError | typeof ProfileError | typeof ContactError | typeof GenericApiError;

export function handleApiErrorTyped(
  error: unknown,
  ErrorClass: ErrorConstructor = GenericApiError,
  fallbackMessage = 'Произошла ошибка',
): InstanceType<ErrorConstructor> {
  if (error instanceof ForecastError || error instanceof ProfileError ||
      error instanceof ContactError || error instanceof GenericApiError) {
    return error as InstanceType<ErrorConstructor>;
  }

  if (isAxiosLike(error)) {
    const status = error.response?.status;
    const serverMsg = error.response?.data?.error || error.response?.data?.message;
    const code = error.response?.data?.code;
    const message = status
      ? mapStatusToMessage(status, serverMsg)
      : error.request !== undefined
        ? 'Нет соединения с сервером'
        : error.message || fallbackMessage;

    return new ErrorClass(message, code, status) as InstanceType<ErrorConstructor>;
  }

  if (error instanceof TypeError) {
    return new ErrorClass('Нет соединения с сервером', 'NETWORK_ERROR') as InstanceType<ErrorConstructor>;
  }

  if (error instanceof Error) {
    return new ErrorClass(error.message, undefined, undefined) as InstanceType<ErrorConstructor>;
  }

  if (typeof error === 'string') {
    return new ErrorClass(error) as InstanceType<ErrorConstructor>;
  }

  return new ErrorClass(fallbackMessage) as InstanceType<ErrorConstructor>;
}

// ============================================================
// parseApiResponse — безопасно парсит JSON ответа
// ============================================================

export async function parseApiResponse<T>(response: Response): Promise<{
  ok: boolean;
  status: number;
  data?: T;
  error?: string;
  code?: string;
}> {
  let body: ErrorResponse & Record<string, unknown>;
  try {
    body = (await response.json()) as ErrorResponse & Record<string, unknown>;
  } catch {
    return {
      ok: false,
      status: response.status,
      error: 'Некорректный ответ сервера',
    };
  }

  if (!response.ok) {
    const serverError = body.error || body.message;
    return {
      ok: false,
      status: response.status,
      error: mapStatusToMessage(response.status, serverError),
      code: body.code,
    };
  }

  return {
    ok: true,
    status: response.status,
    data: body as T,
  };
}
